"use client";
import { useAppContext } from "@/context/AppContext";
import ModalContent from "../_primitives/Modal/ModalContent/ModalContent";
import ModalOverlay from "../_primitives/Modal/ModalOverlay/ModalOverlay";
import ModalRoot from "../_primitives/Modal/ModalRoot/ModalRoot";
import ModalPortal from "../_primitives/Modal/ModalPortal/ModalPortal";
import { useState } from "react";
import styles from "./BookingModal.module.scss";
import FormDynamicWrapper from "@/modules/Form/FormDynamicWrapper";
import { Header_Highlights } from "@aleksundshantu/gp-types/acf-header";
import { ModalClose } from "../_primitives/Modal/ModalClose/ModalClose";
import { CustomLink } from "../CustomLink";
import { LinkType } from "@/types/baseTypes";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import CustomSVG from "../_primitives/CustomSVG/CustomSVG.client";

type BookingModalProps = {
  inquiryFormId: number;
  highlightOverwrite:
    | Header_Highlights["header_highlights"]["booking_options"]
    | null;
  highlightId: number | null;
  bookingLink: LinkType;
};

export const BookingModal = ({
  inquiryFormId,
  highlightOverwrite,
  highlightId,
  bookingLink,
}: BookingModalProps) => {
  const { bookingModal, setBookingModal } = useAppContext();
  const { getLabel } = useGlobalContext();

  const [state, setState] = useState<"initial" | "inquiry">("initial");

  return (
    <ModalRoot
      open={bookingModal.open}
      setOpen={(open) => {
        setBookingModal({ ...bookingModal, open });
      }}
      onMountedChange={(mounted) => {
        // reset after the exit animation, so the content doesn't switch while fading out
        if (!mounted) setState("initial");
      }}
    >
      <ModalPortal>
        <ModalOverlay></ModalOverlay>
        <ModalContent ariaLabel={getLabel("booking_modal", "Buchung")}>
          <ModalClose />

          <div className="section" data-lenis-prevent>
            <div
              className={`${styles["booking-modal"]} container`}
              data-state={state}
            >
              {state === "initial" && (
                <div className={styles["initial"]}>
                  <span>Bitte wählen Sie:</span>
                  <div className={styles["initial-buttons"]}>
                    <button
                      className="button-reset"
                      onClick={() => {
                        setState("inquiry");
                      }}
                    >
                      <CustomSVG
                        iconKey="request-no-bg"
                        wrapper={"span"}
                        width={24}
                        height={24}
                        aria-hidden="true"
                      />
                      <p className="pb-0">
                        {getLabel("booking_modal_request", "Anfrage")}
                      </p>
                    </button>
                    {bookingLink && (
                      <CustomLink
                        href={bookingLink.url}
                        target={bookingLink.target}
                        aria-label={bookingLink.aria_label}
                      >
                        <CustomSVG
                          iconKey="book"
                          wrapper={"span"}
                          width={24}
                          height={24}
                          aria-hidden="true"
                        />
                        <p className="pb-0">
                          {getLabel("booking_modal_book", "Buchen")}
                        </p>
                      </CustomLink>
                    )}
                  </div>
                </div>
              )}

              {state === "inquiry" && (
                <FormDynamicWrapper
                  formId={inquiryFormId}
                  button_type="primary"
                  highlightId={highlightId}
                  highlightsOverwrite={highlightOverwrite}
                />
              )}
            </div>
          </div>
        </ModalContent>
      </ModalPortal>
    </ModalRoot>
  );
};
